import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WsClient } from '@xdocs/ws-protocol';
import type { ConnectionState } from '@xdocs/ws-protocol';
import { useAuth } from '../auth/AuthProvider.js';
import { resolveWsUrl } from '../lib/env.js';
import { createBrowserWsEnvironment } from './browserEnvironment.js';
import { clearBrowserWsPersistence, createBrowserWsPersistence } from './browserPersistence.js';
import { invalidateForPush } from './queryInvalidation.js';
import { createBrowserSocket } from './browserSocketFactory.js';

export interface WsContextValue {
  wsClient: WsClient;
  connectionState: ConnectionState;
}

const WsContext = createContext<WsContextValue | null>(null);

export interface WsProviderProps {
  children: ReactNode;
}

/**
 * Owns the ONE `WsClient` instance for the app (E17: "one endpoint `/ws`").
 * Connects once `AuthProvider` holds an access token, disconnects on
 * logout. Wires every `push` frame into the TanStack Query invalidation
 * skeleton (`queryInvalidation.ts`) so feature hooks just `useQuery` — they
 * never subscribe to a channel themselves for cache invalidation.
 */
export function WsProvider({ children }: WsProviderProps): ReactElement {
  const { status, accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');

  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;

  const clientRef = useRef<WsClient | null>(null);
  if (!clientRef.current) {
    clientRef.current = new WsClient({
      url: resolveWsUrl(),
      createSocket: createBrowserSocket,
      getAccessToken: () => {
        const token = accessTokenRef.current;
        if (!token) return Promise.reject(new Error('no access token'));
        return token;
      },
      persistence: createBrowserWsPersistence(),
      environment: createBrowserWsEnvironment(),
    });
  }
  const client = clientRef.current;

  useEffect(() => {
    const offState = client.on('state', (next) => setConnectionState(next));
    const offPush = client.on('push', (envelope) => invalidateForPush(queryClient, envelope));
    return () => {
      offState();
      offPush();
    };
  }, [client, queryClient]);

  useEffect(() => {
    if (status === 'authenticated' && accessToken) {
      void client.connect();
    } else if (status === 'unauthenticated' && client.getState() !== 'idle' && client.getState() !== 'closed') {
      // Only tear down + wipe persisted state when there was actually a
      // live/reconnecting session to end (e.g. a real logout) — an
      // unauthenticated FIRST mount (fresh visit to /login) never had one,
      // so it shouldn't proactively clear localStorage state on every load.
      client.disconnect();
      clearBrowserWsPersistence();
    }
  }, [client, status, accessToken]);

  // Deliberate teardown only on unmount (app close) — logout is handled by
  // the effect above so a mid-session token clear doesn't wait for unmount.
  useEffect(() => {
    return () => client.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: unmount-only teardown, `client` is a stable ref for the component's lifetime.
  }, []);

  return <WsContext.Provider value={{ wsClient: client, connectionState }}>{children}</WsContext.Provider>;
}

export function useWs(): WsContextValue {
  const ctx = useContext(WsContext);
  if (!ctx) throw new Error('useWs must be used within a <WsProvider>');
  return ctx;
}
